import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Hook für lange Backend-Operationen via Polling.
 *
 * Backend-Flow:
 *   POST /async-tasks/start  → { task_id }
 *   GET  /async-tasks/{id}   → { status, progress, step_label, result, error }
 *
 * Usage:
 *   const { run, taskState, cancel } = useAsyncTask();
 *   const result = await run('newsletter_discover', {});
 */
export default function useAsyncTask() {
  const [taskState, setTaskState] = useState(null);
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setTaskState((t) => t ? { ...t, status: 'cancelled' } : null);
  }, []);

  const run = useCallback(async (taskType, params = {}, { pollIntervalMs = 1500, onUpdate } = {}) => {
    cancelledRef.current = false;
    const { data: startData } = await axios.post(`${API}/async-tasks/start`,
      { task_type: taskType, params },
      { headers: authHeaders() });
    const taskId = startData.task_id;

    return new Promise((resolve, reject) => {
      const tick = async () => {
        if (cancelledRef.current) {
          reject(new Error('cancelled'));
          return;
        }
        try {
          const { data } = await axios.get(`${API}/async-tasks/${taskId}`, { headers: authHeaders() });
          setTaskState(data);
          if (onUpdate) onUpdate(data);
          if (data.status === 'done') resolve(data.result);
          else if (data.status === 'error') reject(new Error(data.error || 'Task fehlgeschlagen'));
          else setTimeout(tick, pollIntervalMs);
        } catch (err) {
          reject(err);
        }
      };
      tick();
    });
  }, []);

  return { run, taskState, cancel };
}
